import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAdminRules } from '@/api/(admin)/rules';
import type { AdminRule } from '@/api/(admin)/rules/types';
import type { RulesQueryInput } from '../-types';
import { buildRulesQs, toRulesParams } from '../-utils/listParams';

/** Prefix for every rules cache entry. */
export const adminRulesQueryKey = ['admin', 'rules'] as const;

/** Shared by the route loader and the page, so the prefetch warms what the page reads. */
export function adminRulesQueryOptions(input: RulesQueryInput) {
  const params = toRulesParams(input);
  return queryOptions({
    queryKey: [...adminRulesQueryKey, 'list', params] as const,
    queryFn: () => fetchAdminRules(buildRulesQs(params)),
  });
}

/**
 * The list.
 *
 * There is no detail fetch behind the sheet, unlike materials and badges:
 * `getById` and `getList` in `db/rules.js` select the same columns, and the
 * list additionally joins the certification's `name`. The row the sheet is
 * opened from is therefore the whole record, so re-fetching it would only add
 * a spinner.
 */
export function useAdminRules(input: RulesQueryInput) {
  const query = useQuery(adminRulesQueryOptions(input));
  const rows: AdminRule[] = query.data?.data ?? [];
  return {
    ...query,
    rows,
    totalCount: query.data?.metadata.pagination.total ?? 0,
  };
}
